// R38 — THE ONE WAY THIS HOUSE LETS HER RENAME SOMETHING OF HERS.
//
// N7-4's law is written on the shelf drawer's own rename handler: "A member can call anything she
// made whatever she wants, whenever she wants, and it sticks." IT WAS TRUE OF ONE DRAWER. Measured at
// R38, the pencil was at ONE of six surfaces that render a row of hers with a name on it:
//
//     the shelf drawer's card      a pencil, an in-place box, N7-4's law working exactly as written
//     My Room, foundational chips  her title, R34's badge, R35's delete — and no way to rename it
//     My Room, on-demand chips     the same
//     My Room, everything else     the same
//     My Room, the tarot list      the same
//     Ask Lala, the thread list    the same, and worse (see below)
//
// MY ROOM IS WHERE SHE LANDS. Those four lists render `entry.title` — HER OWN WORDS wherever she
// named a creation — and the only way to change one was to find the Shelves drawer, pick the right
// room and find the row a second time. That is W12/R26/R35's lesson for the fourth time: the
// capability lived only on the surface that needed it first.
//
// AND THE ASK LALA HALF WAS A CAPABILITY BUILT AND WIRED TO NOTHING. The server has taken
// `POST /api/lala/conversations/:id  { title }` all along and the client had the helper for it —
// `saveConversationField` — with not one call site in the repo. So a saved thread is titled with the
// first 48 characters of her FIRST MESSAGE, verbatim, cut mid-word, permanently: her sidebar is a
// column of her own opening lines and nothing could change one.
//
// THE THREE THINGS THIS DECIDES, and each is the reason it is one control rather than six:
//
//   1. THE SENTENCE IS DERIVED FROM THE KIND. `RENAME_NOUNS` is EXPORTED so a gate asks the TABLE per
//      key rather than reading a returned string — R28/R32's lesson, where a lookup's default
//      collided with a legitimate answer and three real types fell through it in production.
//   2. A BLANK NAME IS NOT A RENAME, AND NEITHER IS AN UNCHANGED ONE. `nextTitle` is PURE and
//      exported, so the gate RUNS it: blank would silently erase what she called it, and unchanged
//      would spend a request to store what is already stored.
//   3. A RENAME THAT FAILED SAYS SO. `onRename` hands back the STORED title (the server's truth, not
//      the string we sent) or null, and null raises the sentence. R35's honesty half: a handler that
//      cannot report failure makes its own error message unreachable code.
import { useState } from "react";
import { Pencil } from "lucide-react";
import { cleanCreationName, MAX_CREATION_NAME } from "../creationName.js";

// WHAT A MEMBER CAN RENAME, in her own words for each thing. There is deliberately no "item" in
// here: the fallback below is not one of these values, so a kind nobody registered reads as a
// fallthrough rather than as a legitimate answer that happens to match (DELETE_NOUNS' rule).
export const RENAME_NOUNS = {
  reading: "reading",           // a saved report — anything on a shelf or in My Room
  conversation: "conversation", // an Ask Lala thread, titled from her first message until she says otherwise
} as const;

export type RenameKind = keyof typeof RENAME_NOUNS;

function nounFor(kind: RenameKind) {
  return RENAME_NOUNS[kind] || "thing";
}

// The screen-reader label names the SPECIFIC row where there is a title to name (a shelf of eight
// readings needs eight distinguishable pencils), and falls back to the kind where there is not.
export function renameAskLabel(kind: RenameKind, what?: string) {
  return `Rename ${(what || "").trim() || `this ${nounFor(kind)}`}`;
}

// What she reads when the server refused. It names the kind, because at that moment the row is still
// on screen wearing its old title and the question is which thing did not change.
export function renameFailedNote(kind: RenameKind) {
  return `That ${nounFor(kind)} could not be renamed right now.`;
}

// THE LAW, PURE AND RUNNABLE. Returns the title to send, or null when this is not a rename at all.
// Blank is refused rather than treated as "clear it" (that would erase her own words on a mis-tap),
// and a title identical to the current one is not a request.
export function nextTitle(current: string, typed: string): string | null {
  const title = cleanCreationName(typed);
  if (!title) return null;
  if (title === cleanCreationName(current)) return null;
  return title;
}

export function RenameControl({ kind, value, what, onRename, className = "" }: {
  kind: RenameKind;
  value: string;
  what?: string;
  onRename: (title: string) => Promise<string | null>;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function save() {
    const title = nextTitle(value, text);
    if (!title) { setEditing(false); return; }
    setBusy(true);
    const stored = await onRename(title);
    setBusy(false);
    if (!stored) { setFailed(true); return; }
    setEditing(false);
  }

  if (editing) {
    return (
      <form
        className={`rename-control rename-control--editing ${className}`.trim()}
        onSubmit={(e) => { e.preventDefault(); void save(); }}
      >
        {/* Pre-filled with what it is called now, so she edits her own words instead of retyping
            them. Enter saves, Escape leaves it alone. */}
        <input
          className="rename-control__input"
          type="text"
          value={text}
          autoFocus
          maxLength={MAX_CREATION_NAME}
          onChange={(e) => { setText(e.target.value); setFailed(false); }}
          onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
          aria-label={renameAskLabel(kind, what || value)}
        />
        <button type="button" className="rename-control__no" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
        <button type="submit" className="rename-control__yes" disabled={busy}>{busy ? "Saving..." : "Save"}</button>
        {failed ? <span className="rename-control__failed">{renameFailedNote(kind)}</span> : null}
      </form>
    );
  }
  return (
    <span className={`rename-control ${className}`.trim()}>
      <button
        type="button"
        className="rename-control__open"
        onClick={() => { setFailed(false); setText(value); setEditing(true); }}
        aria-label={renameAskLabel(kind, what || value)}
      >
        <Pencil size={14} />
      </button>
    </span>
  );
}
